import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as R from "reports/Report";
import * as RD from "reports/ReportDef";
import * as RDPD from "reports/ReportDataProcessorDef";
import * as T from "reports/measures/Trajectory";

/**
 * Calculates trajectories from raw objective outcomes data.
 * This is the shared logic used by both ReportProcessor and validation tools.
 *
 * @param objectiveOutcomes - The raw objective outcomes data from the API
 * @param reportWeek - The report week to filter measures for
 * @returns Array of calculated trajectories
 */
export function calculateTrajectories(
    objectiveOutcomes: any,
    reportWeek: RD.ReportWeek
): T.Trajectories {
    const allTrajectories: T.Trajectories = [];

    if (!objectiveOutcomes) {
        return allTrajectories;
    }

    // Initialize measure arrays
    let leftAbductionMeasures: MD.Measures = [];
    let leftFlexionMeasures: MD.Measures = [];
    let leftScaptionMeasures: MD.Measures = [];
    let rightAbductionMeasures: MD.Measures = [];
    let rightFlexionMeasures: MD.Measures = [];
    let rightScaptionMeasures: MD.Measures = [];
    let lowerFlexionMeasures: MD.Measures = [];
    let neckExtensionMeasures: MD.Measures = [];
    let neckFlexionMeasures: MD.Measures = [];
    let neckLeftRotationMeasures: MD.Measures = [];
    let neckRightRotationMeasures: MD.Measures = [];
    let activityMeasures: MD.Measures = [];
    let confidenceMeasures: MD.Measures = [];
    let dashMeasures: MD.Measures = [];
    let fabqActivityMeasures: MD.Measures = [];
    let fabqTotalMeasures: MD.Measures = [];
    let fabqWorkMeasures: MD.Measures = [];
    let gad7Measures: MD.Measures = [];
    let karunaRatingMeasures: MD.Measures = [];
    let moodMeasures: MD.Measures = [];
    let ndiMeasures: MD.Measures = [];
    let oswestryMeasures: MD.Measures = [];
    let pcsMeasures: MD.Measures = [];
    let pcssMeasures: MD.Measures = [];
    let phq9Measures: MD.Measures = [];
    let pseqMeasures: MD.Measures = [];
    let painMeasures: MD.Measures = [];
    let sleepMeasures: MD.Measures = [];
    let uefiMeasures: MD.Measures = [];
    let whodasMeasures: MD.Measures = [];

    // Populate ROM Measures
    const romMeasurements = objectiveOutcomes?.rom_improvements;
    if (romMeasurements) {
        Object.entries(romMeasurements).forEach(([weekStr, weekData]: [string, RDPD.RomImprovementWeek]) => {
            let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
            const leftArm = weekData.upper_extremity?.left;
            if (leftArm?.abduction?.max_measurement !== undefined && leftArm.abduction.max_measurement > -1) {
                leftAbductionMeasures.push({ type: MD.MeasureTypesMap.LeftAbduction, week: measureWeek, value: Math.round(leftArm.abduction.max_measurement) });
            }
            if (leftArm?.flexion?.max_measurement !== undefined && leftArm.flexion.max_measurement > -1) {
                leftFlexionMeasures.push({ type: MD.MeasureTypesMap.LeftFlexion, week: measureWeek, value: Math.round(leftArm.flexion.max_measurement) });
            }
            if (leftArm?.scaption?.max_measurement !== undefined && leftArm.scaption.max_measurement > -1) {
                leftScaptionMeasures.push({ type: MD.MeasureTypesMap.LeftScaption, week: measureWeek, value: Math.round(leftArm.scaption.max_measurement) });
            }

            const rightArm = weekData.upper_extremity?.right;
            if (rightArm?.abduction?.max_measurement !== undefined && rightArm.abduction.max_measurement > -1) {
                rightAbductionMeasures.push({ type: MD.MeasureTypesMap.RightAbduction, week: measureWeek, value: Math.round(rightArm.abduction.max_measurement) });
            }
            if (rightArm?.flexion?.max_measurement !== undefined && rightArm.flexion.max_measurement > -1) {
                rightFlexionMeasures.push({ type: MD.MeasureTypesMap.RightFlexion, week: measureWeek, value: Math.round(rightArm.flexion.max_measurement) });
            }
            if (rightArm?.scaption?.max_measurement !== undefined && rightArm.scaption.max_measurement > -1) {
                rightScaptionMeasures.push({ type: MD.MeasureTypesMap.RightScaption, week: measureWeek, value: Math.round(rightArm.scaption.max_measurement) });
            }

            const lowerBack = weekData.lower_back;
            if (lowerBack?.flexion?.max_measurement !== undefined && lowerBack.flexion.max_measurement > -1) {
                lowerFlexionMeasures.push({ type: MD.MeasureTypesMap.LowerFlexion, week: measureWeek, value: Math.round(lowerBack.flexion.max_measurement) });
            }

            const neck = weekData.neck;
            if (neck?.extension?.max_measurement !== undefined && neck.extension.max_measurement > -1) {
                neckExtensionMeasures.push({ type: MD.MeasureTypesMap.NeckExtension, week: measureWeek, value: Math.round(neck.extension.max_measurement) });
            }
            if (neck?.flexion?.max_measurement !== undefined && neck.flexion.max_measurement > -1) {
                neckFlexionMeasures.push({ type: MD.MeasureTypesMap.NeckFlexion, week: measureWeek, value: Math.round(neck.flexion.max_measurement) });
            }
            if (neck?.left?.rotation?.max_measurement !== undefined && neck.left.rotation.max_measurement > -1) {
                neckLeftRotationMeasures.push({ type: MD.MeasureTypesMap.NeckLeftRotation, week: measureWeek, value: Math.round(neck.left.rotation.max_measurement) });
            }
            if (neck?.right?.rotation?.max_measurement !== undefined && neck.right.rotation.max_measurement > -1) {
                neckRightRotationMeasures.push({ type: MD.MeasureTypesMap.NeckRightRotation, week: measureWeek, value: Math.round(neck.right.rotation.max_measurement) });
            }
        });
    }

    // Populate non-ROM Measures
    const measuresMap: { [key in MD.MeasureType]?: MD.Measures } = {
        [MD.MeasureTypesMap.Activity]: activityMeasures,
        [MD.MeasureTypesMap.Confidence]: confidenceMeasures,
        [MD.MeasureTypesMap.DASH]: dashMeasures,
        [MD.MeasureTypesMap.FABQActivity]: fabqActivityMeasures,
        [MD.MeasureTypesMap.FABQTotal]: fabqTotalMeasures,
        [MD.MeasureTypesMap.FABQWork]: fabqWorkMeasures,
        [MD.MeasureTypesMap.GAD7]: gad7Measures,
        [MD.MeasureTypesMap.Mood]: moodMeasures,
        [MD.MeasureTypesMap.NDI]: ndiMeasures,
        [MD.MeasureTypesMap.Oswestry]: oswestryMeasures,
        [MD.MeasureTypesMap.PCS]: pcsMeasures,
        [MD.MeasureTypesMap.PCSS]: pcssMeasures,
        [MD.MeasureTypesMap.PHQ9]: phq9Measures,
        [MD.MeasureTypesMap.PSEQ]: pseqMeasures,
        [MD.MeasureTypesMap.Pain]: painMeasures,
        [MD.MeasureTypesMap.Sleep]: sleepMeasures,
        [MD.MeasureTypesMap.UEFI]: uefiMeasures,
        [MD.MeasureTypesMap.WHODAS]: whodasMeasures
    };

    const sampleLabelToMeasureDetailsMap = new Map<string, { type: MD.MeasureType; props: MP.MeasureProperties }>();
    Object.entries(MP.MeasurePropertiesMap).forEach(([typeKey, props]) => {
        const measureType = MD.MeasureTypesMap[typeKey as keyof typeof MD.MeasureTypesMap];
        const measureProps = props as MP.MeasureProperties;
        if (measureType && measureProps) {
            const sampleLabel = MP.MeasureTypeToSampleLabelMap[measureType];
            if (sampleLabel) {
                sampleLabelToMeasureDetailsMap.set(sampleLabel, { type: measureType, props: measureProps });
            }
        }
    });

    const otherMeasurements = objectiveOutcomes?.measurements;
    if (otherMeasurements) {
        Object.entries(otherMeasurements).forEach(([weekStr, weekItems]: [string, RDPD.MeasurementItem[]]) => {
            let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
            weekItems.forEach((item: RDPD.MeasurementItem) => {
                const measureDetails = sampleLabelToMeasureDetailsMap.get(item.label);
                if (measureDetails) {
                    const { type } = measureDetails;
                    const targetMeasuresArray = measuresMap[type];
                    if (targetMeasuresArray) {
                        let numericValue = parseFloat(String(item.value));
                        if (!isNaN(numericValue)) {
                            if (type === MD.MeasureTypesMap.DASH) {
                                numericValue = Math.round(((numericValue / 30) - 1) * 25);
                                if (isNaN(numericValue)) numericValue = 0;
                            }
                            targetMeasuresArray.push({ type: type, week: measureWeek, value: numericValue });
                        }
                    }
                }
            });
        });
    }

    // Populate KarunaRating Measures
    const karunaRatings = objectiveOutcomes?.ratings;
    if (karunaRatings) {
        karunaRatingMeasures = Object.entries(karunaRatings).map(([measureWeekStr, value]) => ({
            type: MD.MeasureTypesMap.KarunaRating,
            week: M.getMeasureWeek(measureWeekStr),
            value
        } as MD.Measure));
    }

    // Filter measures to include in the report
    const filterMeasuresForReport = (measures: MD.Measures): MD.Measures => {
        if (!measures || measures.length === 0) return [];
        const lib = M.lowerIsBetter(M.type(measures));
        const bestMeasurePerWeek: { [weekKey: string]: MD.Measure } = {};
        for (const currentMeasure of measures) {
            const measureWeek: MD.MeasureWeek = currentMeasure.week;
            if (R.includeMeasureWeekInReport(reportWeek, measureWeek)) {
                const measureWeekKey: string = measureWeek.toString();
                const existingBest = bestMeasurePerWeek[measureWeekKey];
                if (!existingBest) {
                    bestMeasurePerWeek[measureWeekKey] = currentMeasure;
                } else {
                    const isCurrentBetter = lib ? currentMeasure.value < existingBest.value : currentMeasure.value > existingBest.value;
                    if (isCurrentBetter) bestMeasurePerWeek[measureWeekKey] = currentMeasure;
                }
            }
        }
        const bestMeasuresArray = Object.values(bestMeasurePerWeek);
        const weekSorter = (a: MD.Measure, b: MD.Measure): number => {
            const aIsMonth6 = a.week === MD.Month6Token;
            const bIsMonth6 = b.week === MD.Month6Token;
            if (aIsMonth6 && bIsMonth6) return 0;
            if (aIsMonth6) return 1;
            if (bIsMonth6) return -1;
            return (a.week as number) - (b.week as number);
        };
        return bestMeasuresArray.sort(weekSorter);
    };

    // Apply filters to all measure arrays
    leftAbductionMeasures = filterMeasuresForReport(leftAbductionMeasures);
    leftFlexionMeasures = filterMeasuresForReport(leftFlexionMeasures);
    leftScaptionMeasures = filterMeasuresForReport(leftScaptionMeasures);
    rightAbductionMeasures = filterMeasuresForReport(rightAbductionMeasures);
    rightFlexionMeasures = filterMeasuresForReport(rightFlexionMeasures);
    rightScaptionMeasures = filterMeasuresForReport(rightScaptionMeasures);
    lowerFlexionMeasures = filterMeasuresForReport(lowerFlexionMeasures);
    neckExtensionMeasures = filterMeasuresForReport(neckExtensionMeasures);
    neckFlexionMeasures = filterMeasuresForReport(neckFlexionMeasures);
    neckLeftRotationMeasures = filterMeasuresForReport(neckLeftRotationMeasures);
    neckRightRotationMeasures = filterMeasuresForReport(neckRightRotationMeasures);
    activityMeasures = filterMeasuresForReport(activityMeasures);
    confidenceMeasures = filterMeasuresForReport(confidenceMeasures);
    dashMeasures = filterMeasuresForReport(dashMeasures);
    fabqActivityMeasures = filterMeasuresForReport(fabqActivityMeasures);
    fabqTotalMeasures = filterMeasuresForReport(fabqTotalMeasures);
    fabqWorkMeasures = filterMeasuresForReport(fabqWorkMeasures);
    gad7Measures = filterMeasuresForReport(gad7Measures);
    karunaRatingMeasures = filterMeasuresForReport(karunaRatingMeasures);
    moodMeasures = filterMeasuresForReport(moodMeasures);
    ndiMeasures = filterMeasuresForReport(ndiMeasures);
    oswestryMeasures = filterMeasuresForReport(oswestryMeasures);
    pcsMeasures = filterMeasuresForReport(pcsMeasures);
    pcssMeasures = filterMeasuresForReport(pcssMeasures);
    phq9Measures = filterMeasuresForReport(phq9Measures);
    pseqMeasures = filterMeasuresForReport(pseqMeasures);
    painMeasures = filterMeasuresForReport(painMeasures);
    sleepMeasures = filterMeasuresForReport(sleepMeasures);
    uefiMeasures = filterMeasuresForReport(uefiMeasures);
    whodasMeasures = filterMeasuresForReport(whodasMeasures);

    // Get first measurements for ROM trajectories
    const romFirstMeasurements = objectiveOutcomes?.rom_first_measurements;
    const ueLeftFirstMeasurements = romFirstMeasurements?.upper_extremity?.left;
    const ueRightFirstMeasurements = romFirstMeasurements?.upper_extremity?.right;
    const lbFirstMeasurements = romFirstMeasurements?.lower_back;
    const neckFirstMeasurements = romFirstMeasurements?.neck;

    // Process trajectories
    const processTrajectories = (processors: T.TrajectoryProcessor[]) => {
        processors?.forEach(processor => {
            const trajectory: T.Trajectory = T.createTrajectory(processor);
            if (trajectory) allTrajectories.push(trajectory);
        });
    };

    processTrajectories([
        // Upper Extremity ROM Measures
        { measureType: MD.MeasureTypesMap.LeftAbduction, measures: leftAbductionMeasures, firstMeasurement: ueLeftFirstMeasurements?.abduction },
        { measureType: MD.MeasureTypesMap.LeftFlexion, measures: leftFlexionMeasures, firstMeasurement: ueLeftFirstMeasurements?.flexion },
        { measureType: MD.MeasureTypesMap.LeftScaption, measures: leftScaptionMeasures, firstMeasurement: ueLeftFirstMeasurements?.scaption },
        { measureType: MD.MeasureTypesMap.RightAbduction, measures: rightAbductionMeasures, firstMeasurement: ueRightFirstMeasurements?.abduction },
        { measureType: MD.MeasureTypesMap.RightFlexion, measures: rightFlexionMeasures, firstMeasurement: ueRightFirstMeasurements?.flexion },
        { measureType: MD.MeasureTypesMap.RightScaption, measures: rightScaptionMeasures, firstMeasurement: ueRightFirstMeasurements?.scaption },
        // Lower Back ROM Measures
        { measureType: MD.MeasureTypesMap.LowerFlexion, measures: lowerFlexionMeasures, firstMeasurement: lbFirstMeasurements?.flexion },
        // Neck ROM Measures
        { measureType: MD.MeasureTypesMap.NeckExtension, measures: neckExtensionMeasures, firstMeasurement: neckFirstMeasurements?.extension },
        { measureType: MD.MeasureTypesMap.NeckFlexion, measures: neckFlexionMeasures, firstMeasurement: neckFirstMeasurements?.flexion },
        { measureType: MD.MeasureTypesMap.NeckLeftRotation, measures: neckLeftRotationMeasures, firstMeasurement: neckFirstMeasurements?.left?.rotation },
        { measureType: MD.MeasureTypesMap.NeckRightRotation, measures: neckRightRotationMeasures, firstMeasurement: neckFirstMeasurements?.right?.rotation },
        // Non-ROM Measures
        { measureType: MD.MeasureTypesMap.Activity, measures: activityMeasures },
        { measureType: MD.MeasureTypesMap.Confidence, measures: confidenceMeasures },
        { measureType: MD.MeasureTypesMap.DASH, measures: dashMeasures },
        { measureType: MD.MeasureTypesMap.FABQActivity, measures: fabqActivityMeasures },
        { measureType: MD.MeasureTypesMap.FABQTotal, measures: fabqTotalMeasures },
        { measureType: MD.MeasureTypesMap.FABQWork, measures: fabqWorkMeasures },
        { measureType: MD.MeasureTypesMap.GAD7, measures: gad7Measures },
        { measureType: MD.MeasureTypesMap.KarunaRating, measures: karunaRatingMeasures },
        { measureType: MD.MeasureTypesMap.Mood, measures: moodMeasures },
        { measureType: MD.MeasureTypesMap.NDI, measures: ndiMeasures },
        { measureType: MD.MeasureTypesMap.Oswestry, measures: oswestryMeasures },
        { measureType: MD.MeasureTypesMap.PCS, measures: pcsMeasures },
        { measureType: MD.MeasureTypesMap.PCSS, measures: pcssMeasures },
        { measureType: MD.MeasureTypesMap.PHQ9, measures: phq9Measures },
        { measureType: MD.MeasureTypesMap.PSEQ, measures: pseqMeasures },
        { measureType: MD.MeasureTypesMap.Pain, measures: painMeasures },
        { measureType: MD.MeasureTypesMap.Sleep, measures: sleepMeasures },
        { measureType: MD.MeasureTypesMap.UEFI, measures: uefiMeasures },
        { measureType: MD.MeasureTypesMap.WHODAS, measures: whodasMeasures }
    ]);

    // Sort trajectories by normalized delta
    if (allTrajectories.length > 0) {
        allTrajectories.sort((a, b) => b.normalizedDelta - a.normalizedDelta);
    }

    return allTrajectories;
}
